// 261005 E4-P1 — mô hình THUẦN của chế độ "Sửa kịch bản" (thêm/xoá/sửa cảnh và dòng). KHÔNG import antd (jest của CRA không nạp được antd v6).
// Luật round-trip: bản nháp = BẢN SAO SÂU của kịch bản đang lưu; mọi hàm chỉ đổi đúng trường được sửa (spread phần còn lại) —
// trường ẩn (timeOfDay, want, sourceEvidence, subtext, sourceQuote…) đi nguyên vẹn. Mọi hàm TRẢ BẢN MỚI, không đổi đầu vào.
import type { EpisodeScriptRecord, FilmOutline, ScriptBeat, ScriptBeatKind, ScriptScene } from '../../../types/adaptation';
import { isIntroBeat } from './introLineView';

export type ScriptDoc = NonNullable<EpisodeScriptRecord['script']>;

export const MANUAL_SCENE_PREFIX = 'manual_sc_';
export const isManualScene = (sc: Pick<ScriptScene, 'scenePlanId'> | null | undefined): boolean => String(sc?.scenePlanId ?? '').startsWith(MANUAL_SCENE_PREFIX);

const str = (v: unknown): string => String(v ?? '').trim();

/** Bản sao sâu (JSON thuần) — nguồn của bản nháp. */
export const cloneScript = <T,>(s: T): T => JSON.parse(JSON.stringify(s)) as T;

const stable = (v: unknown): string => JSON.stringify(v, (_k, val) => (
  val && typeof val === 'object' && !Array.isArray(val)
    ? Object.keys(val).sort().reduce((o: Record<string, unknown>, k) => { o[k] = (val as Record<string, unknown>)[k]; return o; }, {})
    : val));

/** Có khác bản gốc không (so JSON chuẩn hoá, thứ tự khoá không tính). */
export const isDirty = (original: unknown, draft: unknown): boolean => stable(original) !== stable(draft);

// ── cảnh ─────────────────────────────────────────────────────────────────────────────────────

/** Cảnh thêm tay: id tổng hợp `manual_sc_<nowId>` (id truyền vào để test được), `origin: ADAPTATION_ADDED`, chưa có dòng. */
export const createManualScene = (defaultLocationId: string, nowId: string): ScriptScene => ({
  scenePlanId: `${MANUAL_SCENE_PREFIX}${nowId}`, origin: 'ADAPTATION_ADDED', locationId: defaultLocationId, characterIds: [], action: '', beats: [],
});

/** Chèn cảnh tại `index` (kẹp 0..n). `timeOfDay` thiếu ⇒ chép từ cảnh liền trước (không có ⇒ bỏ trống). */
export const insertScene = (script: ScriptDoc, index: number, scene: ScriptScene): ScriptDoc => {
  const scenes = [...script.scenes];
  const at = Math.max(0, Math.min(index, scenes.length));
  const prevTime = at > 0 ? str(scenes[at - 1]?.timeOfDay) : '';
  const next: ScriptScene = !str(scene.timeOfDay) && prevTime ? { ...scene, timeOfDay: prevTime } : scene;
  scenes.splice(at, 0, next);
  return { ...script, scenes };
};

/** Xoá cảnh; còn 1 cảnh ⇒ ném lỗi (tập không được rỗng). */
export const removeScene = (script: ScriptDoc, index: number): ScriptDoc => {
  if (script.scenes.length <= 1) throw new Error('Tập phải còn ít nhất 1 cảnh');
  if (index < 0 || index >= script.scenes.length) return script;
  return { ...script, scenes: script.scenes.filter((_, i) => i !== index) };
};

const moved = <T,>(arr: readonly T[], from: number, to: number): T[] | null => {
  if (from === to || from < 0 || to < 0 || from >= arr.length || to >= arr.length) return null;
  const out = [...arr];
  const [x] = out.splice(from, 1);
  out.splice(to, 0, x);
  return out;
};

/** Dời cảnh `from` → `to`; ra ngoài biên / cùng chỗ ⇒ trả nguyên bản. */
export const moveScene = (script: ScriptDoc, from: number, to: number): ScriptDoc => {
  const scenes = moved(script.scenes, from, to);
  return scenes ? { ...script, scenes } : script;
};

/** Sửa trường editor được phép sửa của một cảnh. */
export const updateScene = (script: ScriptDoc, index: number, patch: Partial<Pick<ScriptScene, 'locationId' | 'action'>>): ScriptDoc => {
  if (index < 0 || index >= script.scenes.length) return script;
  return { ...script, scenes: script.scenes.map((sc, i) => (i === index ? { ...sc, ...patch } : sc)) };
};

// ── dòng ─────────────────────────────────────────────────────────────────────────────────────

/** Thêm dòng cuối cảnh (mặc định thoại, chưa có người nói/chữ — `clientValidate` sẽ chặn Lưu tới khi điền). */
export const addBeat = (scene: ScriptScene, kind: ScriptBeatKind = 'dialogue', characterId = ''): ScriptScene => ({
  ...scene, beats: [...scene.beats, { kind, ...(kind === 'action' ? {} : { characterId }), text: '' }],
});

/**
 * Sửa dòng: chỉ `kind`/`characterId`/`text` (subtext, sourceQuote… giữ nguyên). 261015 P4c-2c-2 (Q5): dòng V.O. giới thiệu (`introLineId`) CHỈ ĐỌC — mọi patch bị bỏ
 * (server từ chối 422 nếu đổi); FE không bao giờ ghi `introLineId`/`voiceoverPurpose` nên không tạo được dòng giới thiệu mới.
 */
export const updateBeat = (scene: ScriptScene, index: number, patch: Partial<Pick<ScriptBeat, 'kind' | 'characterId' | 'speakerLabel' | 'text'>>): ScriptScene => {
  if (index < 0 || index >= scene.beats.length) return scene;
  if (isIntroBeat(scene.beats[index])) return scene;
  return { ...scene, beats: scene.beats.map((b, i) => (i === index ? { ...b, ...patch } : b)) };
};

export const removeBeat = (scene: ScriptScene, index: number): ScriptScene => (
  index < 0 || index >= scene.beats.length ? scene : { ...scene, beats: scene.beats.filter((_, i) => i !== index) });

export const moveBeat = (scene: ScriptScene, from: number, to: number): ScriptScene => {
  const beats = moved(scene.beats, from, to);
  return beats ? { ...scene, beats } : scene;
};

// ── cảnh báo khi xoá / kiểm trước khi gửi ────────────────────────────────────────────────────

const EXIT_SCENES = 2;
/** Cảnh `index` nằm trong 2 cảnh cuối (đoạn kết dùng để nối tập). */
export const isExitScene = (script: ScriptDoc, index: number): boolean => index >= 0 && index >= script.scenes.length - EXIT_SCENES;

/** Câu cảnh báo trước khi XOÁ cảnh (rỗng ⇒ xoá thẳng): khoảnh khắc bắt buộc (theo dàn cảnh hoặc chính cảnh) · đoạn kết. */
export const removeSceneWarning = (script: ScriptDoc, index: number): string[] => {
  const sc = script.scenes[index];
  if (!sc) return [];
  const out: string[] = [];
  const plan = (script.scenePlan ?? []).find((p) => p.id === sc.scenePlanId) as { requiredMomentIds?: string[] } | undefined;
  const ids = [...(plan?.requiredMomentIds ?? []), ...(sc.requiredMomentIds ?? [])].filter(Boolean);
  if (ids.length) out.push('Cảnh này chứa khoảnh khắc bắt buộc của truyện — xoá đi có thể làm mất khoảnh khắc đó.');
  if (isExitScene(script, index)) out.push('Xoá/sửa cảnh cuối sẽ làm đổi đoạn kết — hệ thống sẽ kiểm nối với tập sau.');
  return out;
};

/** Lỗi chặn nhanh trước khi gửi (server vẫn là chốt thật). Rỗng ⇒ gửi được. */
export const clientValidate = (script: ScriptDoc, outline: FilmOutline | null): string[] => {
  const errs: string[] = [];
  if (!script.scenes.length) return ['Tập chưa có cảnh nào'];
  const locs = outline ? new Set(outline.locations.map((l) => l.id)) : null;
  const chars = outline ? new Set(outline.characters.map((c) => c.id)) : null;
  script.scenes.forEach((sc, i) => {
    const at = `Cảnh ${i + 1}`;
    if (!str(sc.locationId)) errs.push(`${at}: chưa chọn bối cảnh`);
    else if (locs && !locs.has(sc.locationId)) errs.push(`${at}: bối cảnh "${sc.locationId}" không có trong khung xương`);
    if (isManualScene(sc) && !str(sc.action)) errs.push(`${at}: cảnh thêm tay cần mô tả hành động`);
    if (!sc.beats.length) errs.push(`${at}: chưa có dòng nào`);
    sc.beats.forEach((b, j) => {
      const bat = `${at}, dòng ${j + 1}`;
      if (!str(b.text)) errs.push(`${bat}: chữ rỗng`);
      if (b.kind === 'dialogue' && !str(b.characterId) && !str(b.speakerLabel)) errs.push(`${bat}: lời thoại chưa có người nói`);
      if (str(b.characterId) && chars && !chars.has(str(b.characterId))) errs.push(`${bat}: người nói "${b.characterId}" không có trong khung xương`);
    });
  });
  return errs;
};

/** Lột cờ INTRO khỏi beat KHÔNG phải `voiceover` (BE cũng lột ở đường sinh tập; cờ lạc trên thoại/hành động sẽ làm `MANUAL_INTRO_INVALID` ở mọi lần lưu). Không có cờ ⇒ trả nguyên đối tượng. */
const stripStrayIntroFlags = (b: ScriptBeat): ScriptBeat => {
  if (b.kind === 'voiceover' || (b.introLineId === undefined && b.voiceoverPurpose === undefined)) return b;
  const { introLineId: _i, voiceoverPurpose: _v, ...rest } = b;   // eslint-disable-line @typescript-eslint/no-unused-vars
  return rest;
};

/**
 * Trước khi gửi: cảnh thêm tay tính lại `characterIds` = tập người nói của các dòng (cảnh có sẵn giữ nguyên);
 * 261015 P4c-2c-2 (review MINOR-1): mọi beat không phải V.O. bị lột `introLineId`/`voiceoverPurpose`. V.O. (kể cả INTRO) đi nguyên.
 */
export const prepareForSave = (script: ScriptDoc): ScriptDoc => ({
  ...script,
  scenes: script.scenes.map((sc0) => {
    const beats = sc0.beats.map(stripStrayIntroFlags);
    const sc = beats.some((x, k) => x !== sc0.beats[k]) ? { ...sc0, beats } : sc0;
    if (!isManualScene(sc)) return sc;
    const ids: string[] = [];
    for (const bt of sc.beats) { const id = str(bt.characterId); if (id && !ids.includes(id)) ids.push(id); }
    return { ...sc, characterIds: ids };
  }),
});
